import React from "react";
import {
  Grid,
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import {
  InsertDriveFile as FileIcon,
  Description as JsonIcon,
  TableChart as CsvIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";

// Helper function to infer AJV types from sample values
const inferAjvType = (value) => {
  if (value === null || value === undefined || value === "") {
    return "string";
  }
  // Check if it's a plain object
  if (typeof value === "object" && value.constructor === Object) {
    return "object";
  }
  const strValue = String(value).trim();

  // Check for boolean
  if (strValue.toLowerCase() === "true" || strValue.toLowerCase() === "false") {
    return "boolean";
  }

  // Check for integer
  if (/^-?\d+$/.test(strValue)) {
    return "integer";
  }

  // Check for number (including decimals)
  if (/^-?\d*\.?\d+$/.test(strValue)) {
    return "number";
  }

  // Check for date patterns
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue) || /^\d{2}\/\d{2}\/\d{4}/.test(strValue)) {
    return "string (date)";
  }

  // Check for email
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
    return "string (email)";
  }

  // Check for URL
  if (/^https?:\/\//.test(strValue)) {
    return "string (url)";
  }

  // Default to string
  return "string";
};

// Helper function to get AJV schema properties from inferred type
const getAjvSchemaProperties = (value) => {
  if (value === null || value === undefined || value === "") {
    return { type: "string" };
  }

  const strValue = String(value).trim();

  // Check for boolean
  if (strValue.toLowerCase() === "true" || strValue.toLowerCase() === "false") {
    return { type: "boolean" };
  }

  // Check for integer
  if (/^-?\d+$/.test(strValue)) {
    return { type: "integer" };
  }

  // Check for number (including decimals)
  if (/^-?\d*\.?\d+$/.test(strValue)) {
    return { type: "number" };
  }

  // Check for date patterns
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
    return { type: "string", format: "date" };
  }

  // Check for email
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
    return { type: "string", format: "email" };
  }

  // Check for URL
  if (/^https?:\/\//.test(strValue)) {
    return { type: "string", format: "url" };
  }

  // Default to string
  return { type: "string" };
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const DataSelector = ({ filePreview, fileMetadata, onFileChange, onLoadConfiguration }) => {
  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const { files } = e.dataTransfer;
    if (files.length > 0) {
      const file = files[0];
      // Check if file type is valid
      if (
        file.type === "text/csv" ||
        file.type === "application/json" ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".json")
      ) {
        // Create a synthetic event to mimic file input change
        const syntheticEvent = {
          target: {
            files: [file],
          },
        };
        onFileChange(syntheticEvent);
      }
    }
  };
  const getFileIcon = () => {
    if (!fileMetadata) return <FileIcon />;

    if (fileMetadata.type === "text/csv" || fileMetadata.name.endsWith(".csv")) {
      return <CsvIcon color="success" />;
    }
    if (fileMetadata.type === "application/json" || fileMetadata.name.endsWith(".json")) {
      return <JsonIcon color="primary" />;
    }
    return <FileIcon />;
  };

  const getFileType = () => {
    if (!fileMetadata) return "Unknown";

    if (fileMetadata.type === "text/csv" || fileMetadata.name.endsWith(".csv")) {
      return "CSV";
    }
    if (fileMetadata.type === "application/json" || fileMetadata.name.endsWith(".json")) {
      return "JSON";
    }
    return "Unknown";
  };

  return (
    <Grid container spacing={2}>
      <Grid size={6}>
        {!fileMetadata ? (
          <Paper
            variant="outlined"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              height: 600,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              border: isDragOver ? "2px dashed #1976d2" : "2px dashed #ccc",
              backgroundColor: isDragOver ? "#f3f8ff" : "transparent",
              transition: "all 0.3s ease",
              cursor: "pointer",
              "&:hover": {
                borderColor: "#1976d2",
                backgroundColor: "#f9f9f9",
              },
            }}
          >
            <UploadIcon sx={{ fontSize: 64, color: isDragOver ? "#1976d2" : "#ccc", mb: 2 }} />
            <Typography variant="h6" color={isDragOver ? "primary" : "textSecondary"} gutterBottom>
              {isDragOver ? "Drop your file here" : "Drag & Drop Your Data File"}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: "center" }}>
              Supports CSV and JSON files
              <br />
              or click the button below to browse
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
              <Button variant="contained" component="label" size="large">
                Select Data File
                <input hidden accept=".csv,application/json" type="file" onChange={onFileChange} />
              </Button>
              {onLoadConfiguration && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    - OR -
                  </Typography>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onLoadConfiguration}
                    sx={{ minWidth: 200 }}
                  >
                    Load Existing Configuration
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        ) : (
          <Card elevation={2} sx={{ height: 600 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box sx={{ mr: 2, fontSize: 40 }}>{getFileIcon()}</Box>
                <Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    File Details
                  </Typography>
                  <Chip label={getFileType()} color="primary" size="small" />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    File Name
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                    {fileMetadata.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    File Size
                  </Typography>
                  <Typography variant="body1">{formatFileSize(fileMetadata.size)}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Modified
                  </Typography>
                  <Typography variant="body1">
                    {new Date(fileMetadata.lastModified).toLocaleString()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Attributes Detected
                  </Typography>
                  <Typography variant="body1">
                    {filePreview && typeof filePreview === "object" && !filePreview.error
                      ? Object.keys(filePreview).length
                      : 0}{" "}
                    fields
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button variant="outlined" component="label" fullWidth>
                  Select Different File
                  <input
                    hidden
                    accept=".csv,application/json"
                    type="file"
                    onChange={onFileChange}
                  />
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Grid>

      <Grid size={6}>
        <Paper
          variant="outlined"
          sx={{
            height: 600,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6">Data Preview</Typography>
            <Typography variant="body2" color="textSecondary">
              Source attributes with inferred types and sample values
            </Typography>
          </Box>

          {filePreview && typeof filePreview === "object" && !filePreview.error ? (
            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Source Attribute</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Inferred Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Sample Value</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(filePreview).map(([key, value]) => (
                    <TableRow key={key} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inferAjvType(value)}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={typeof value === "object" ? JSON.stringify(value) : String(value)}
                        >
                          {value !== null && value !== undefined ? (
                            typeof value === "object" ? (
                              JSON.stringify(value)
                            ) : (
                              String(value)
                            )
                          ) : (
                            <em>empty</em>
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : filePreview?.error ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="error">
                Error Loading File
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {filePreview.error}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: "center", mt: 8 }}>
              <Typography variant="h6" color="textSecondary">
                No Data Preview
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Select a CSV or JSON file to preview the data structure
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DataSelector;
export { getAjvSchemaProperties };
